/**
 * Activities tracking events
 *
 * These events match power-loader canonical schemas:
 * - eventschema/cap1_events.py lines 3679-3719
 * - eventschema/cap1_traits.py lines 594-645
 */

export enum ActivityCommonTrackingEventsName {
  // When user views an activity card in the list
  viewed_activity_in_list = "viewed_activity_in_list",
  // When user views activity details/shop page
  viewed_activity_details = "viewed_activity_details",
  // When user applies filters
  activities_applied_filter = "activities_applied_filter",
  // When user lands on the activities search homepage
  viewed_activities_search = "viewed_activities_search",
}

export const ACTIVITY_PRODUCT_TYPE = "activity" as const;

export type ActivityHTSEventBasicProperties = {
  productType: typeof ACTIVITY_PRODUCT_TYPE;
};

export type ActivityHTSEventProperties<Properties> =
  ActivityHTSEventBasicProperties & Properties;

export const ActivityEventName = {
  ...ActivityCommonTrackingEventsName,
};

export type ActivityEventName = typeof ActivityEventName;

// Properties for viewed_activity_in_list event
export type ViewedActivityInListProperties = {
  productId?: string;
  price?: number;
  sort_index?: number;
  sort_used?: string;
  activity_name?: string;
  // ActivitiesSearchTrait
  advance?: number;
  location_searched?: string;
  filters_applied?: string[];
  tag_selected?: string;
  // ActivitiesTrait
  credit_balance?: number;
  entry_type?: string;
  entry_types_all?: string[];
  environment?: string;
};

// Properties for viewed_activity_details event
export type ViewedActivityDetailsProperties = {
  // ActivitiesDetailsTraits
  activity_provider?: string;
  avg_rating?: number;
  activity_name?: string;
  ag_1?: number;
  ag_2?: number;
  ag_3?: number;
  ag_4?: number;
  ag_5?: number;
  age_groups?: string[];
  markup_percentage?: number;
  number_of_photos?: number;
  number_of_reviews?: number;
  original_net?: number;
  productId?: string;
  product_sort_index?: number;
  total_price?: number;
  rrp?: number;
  // ActivitiesSearchTrait (inherited)
  advance?: number;
  location_searched?: string;
  filters_applied?: string[];
  tag_selected?: string;
  // ActivitiesTrait (inherited)
  credit_balance?: number;
  entry_type?: string;
  entry_types_all?: string[];
  environment?: string;
};

// Properties for activities_applied_filter event
export type ActivitiesAppliedFilterProperties = {
  filter_type?: string;
  // ActivitiesSearchTrait
  advance?: number;
  location_searched?: string;
  filters_applied?: string[];
  tag_selected?: string;
  // ActivitiesTrait
  credit_balance?: number;
  entry_type?: string;
  entry_types_all?: string[];
  environment?: string;
  // Filter detail properties
  categoriesCount?: number;
  priceFilterApplied?: boolean;
  ratingFilterApplied?: boolean;
  timeFilterApplied?: boolean;
  durationFilterApplied?: boolean;
  tagsFilterApplied?: boolean;
};

// Event type definitions
export type ViewedActivityInList = {
  name: ActivityCommonTrackingEventsName.viewed_activity_in_list;
  properties: ActivityHTSEventProperties<ViewedActivityInListProperties>;
};

export type ViewedActivityDetails = {
  name: ActivityCommonTrackingEventsName.viewed_activity_details;
  properties: ActivityHTSEventProperties<ViewedActivityDetailsProperties>;
};

export type ActivitiesAppliedFilter = {
  name: ActivityCommonTrackingEventsName.activities_applied_filter;
  properties: ActivityHTSEventProperties<ActivitiesAppliedFilterProperties>;
};

export type ViewedActivitiesSearch = {
  name: ActivityCommonTrackingEventsName.viewed_activities_search;
  properties: ActivityHTSEventProperties<Record<string, unknown>>;
};

export type ActivityHTSEvent =
  | ViewedActivityInList
  | ViewedActivityDetails
  | ActivitiesAppliedFilter
  | ViewedActivitiesSearch;
